'use client';
import { Suspense, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { SerialNumberHelpDrawer } from 'components/my-equipment/add-equipment/add-equipment-form/serial-number-help-drawer/serial-number-help-drawer';
import { EditEquipmentBySerial } from '../edit-equipment-by-serial';
import {
  Conditional,
  DrawerBody,
  MyEquipmentFooter,
  MyEquipmentNickname,
  MyEquipmentSaveFooter
} from 'components/pcc-components';
import {
  FORM_FIELD_NICK_NAME,
  FORM_FIELD_SERIAL,
  GENERIC_ERROR
} from 'components/my-equipment/constants';
import { DeleteEquipmentButton } from './delete-equipment-button';
import { useMyEquipmentDrawer } from 'components/my-equipment/my-equipment-drawer-context';
import type { SelectedEquipmentInfoProps } from '../types';
import { useFormContext } from 'react-hook-form';
import { ErrorBannerMessage } from 'components/my-equipment/error-banner-message';
import { useFeedback } from 'providers/feedback-provider';
import { CatSkeleton } from 'components/blocks-components';
import { EditEquipmentSerialPrefixSuggestions } from './edit-equipment-serial-prefix-suggestions';
import { useSuggestedPrefixes } from '../suggested-prefixes-context';
import { useParams, usePathname } from 'next/navigation';
import { checkIsAlpPage } from 'components/my-equipment/utils';

export const EditEquipmentDrawerForm = ({
  selectedEquipmentInfo
}: {
  selectedEquipmentInfo: SelectedEquipmentInfoProps;
}) => {
  const t = useTranslations();
  const pathname = usePathname();
  // TODO - Input Validation - Ensure that all values used from params are allowed values:
  // lang should always be a two-digit string without numbers or special characters (like 'es');
  // esite should always be a string without numbers or special characters, except for the dash (like demo-dealer);
  // part-name should be a string restricting generalRestrictedCharacters (@\constants\securityConstants.ts).
  // part-number should be a string restricting generalRestrictedCharacters (@\constants\securityConstants.ts).
  const params = useParams();
  const { isAlpSubpage } = checkIsAlpPage(params, pathname);
  const { showFeedback, feedbackMessage, dismissFeedback } = useFeedback();
  const { editEquipmentDrawer, isLoading: isDrawerLoading } =
    useMyEquipmentDrawer();
  const [isPendingSearchMachine, setIsPendingSearchMachine] = useState(false);
  const { equipmentDetails } = useSuggestedPrefixes();
  const prefixList = equipmentDetails?.suggestedSerialNumberPrefixes
    ? equipmentDetails.suggestedSerialNumberPrefixes
    : [];
  const prefixListCommaDelimited =
    prefixList.length > 0 ? prefixList.join(', ') : '';
  const displaySuggestedPrefixes = equipmentDetails !== undefined;
  const {
    getValues,
    formState: { isDirty, isValid, errors },
    reset,
    clearErrors
  } = useFormContext();
  const handleClose = () => {
    dismissFeedback();
    clearErrors();
    reset();
    editEquipmentDrawer.closeDrawer();
  };
  const isErrorEquipmentBySerial = Boolean(errors[FORM_FIELD_SERIAL]);
  const isSerialLengthValid =
    getValues(FORM_FIELD_SERIAL)?.length > 0 &&
    getValues(FORM_FIELD_SERIAL)?.length < 8;
  const isEditButtonDisabled =
    isPendingSearchMachine ||
    isSerialLengthValid ||
    isErrorEquipmentBySerial ||
    !isValid ||
    !isDirty ||
    (showFeedback && feedbackMessage === GENERIC_ERROR) ||
    isDrawerLoading;

  useEffect(() => {
    return () => {
      dismissFeedback();
    };
  }, [dismissFeedback]);

  return (
    <>
      <DrawerBody>
        <Conditional test={showFeedback}>
          <ErrorBannerMessage
            buttonLabel={t('FOOTER_CONTACT_US')}
            bannerLabel={t('UNKNOWN_ERROR_MESSAGE')}
          />
        </Conditional>
        <EditEquipmentBySerial
          selectedEquipmentInfo={selectedEquipmentInfo}
          setIsPendingSearchMachine={setIsPendingSearchMachine}
        />
        <div className="mt-2 mb-2 pb-1">
          <Conditional
            test={displaySuggestedPrefixes}
            fallback={<CatSkeleton size={'sm'} />}
          >
            <Suspense fallback={null}>
              <EditEquipmentSerialPrefixSuggestions
                suggestedPrefixes={prefixListCommaDelimited}
              />
            </Suspense>
          </Conditional>
        </div>
        <SerialNumberHelpDrawer showBackButton isChild />
        <MyEquipmentNickname
          placeholder={t('MEQ_NICKNAME')}
          name={FORM_FIELD_NICK_NAME}
        />
        <Conditional test={!isAlpSubpage}>
          <DeleteEquipmentButton disableDeleteButton={isDirty} />
        </Conditional>
      </DrawerBody>
      <MyEquipmentFooter>
        <MyEquipmentSaveFooter
          disableSave={isEditButtonDisabled}
          saveLabel={t('SAVE')}
          cancelClass={'ps-3'}
          cancelLabel={t('CANCEL')}
          onCancelHandler={handleClose}
        />
      </MyEquipmentFooter>
    </>
  );
};
